/**
 * CarryForwardModal
 *
 * Shown when the user attempts to transition live → post-trading while any
 * TradeDraft in the session has `carryForwardDecision === 'undecided'`.
 *
 * For each unresolved draft the trader picks:
 *   - Carry Forward — draft will be re-created in the next session
 *   - Discard — draft is closed with this session
 *
 * Default selection per draft:
 *   - `workflowStatus === 'active'` → Carry Forward
 *   - `workflowStatus === 'pending'` → Discard
 *
 * "All: Carry Forward" / "All: Discard" shortcuts apply the same choice to
 * every draft at once (shown only when drafts.length > 1).
 *
 * Confirm writes `carryForwardDecision` via `onConfirm` then the caller
 * proceeds with the phase transition. Cancel leaves the session in live phase.
 *
 * @see TradingHub — opens this modal and handles the confirm/cancel callbacks
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button,
} from '@enfomentis/ui';

// 3. Type imports
import type { TradeDraft } from '@/types/transient/TradeDraft';

// ===== TYPES =====

type Decision = 'carry-forward' | 'do-not-carry';

export interface CarryForwardModalProps {
  isOpen: boolean;
  /** Cancel — closes modal, session stays in live phase */
  onClose: () => void;
  /** Confirm — called with per-draft decisions; caller persists then transitions */
  onConfirm: (decisions: Record<string, Decision>) => Promise<void>;
  /** Unresolved drafts only (carryForwardDecision === 'undecided') */
  drafts: TradeDraft[];
  /** True while onConfirm is in flight */
  isCommitting: boolean;
}

// ===== COMPONENT =====

export const CarryForwardModal: React.FC<CarryForwardModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  drafts,
  isCommitting,
}) => {
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  // Seed default decisions whenever the modal opens or the drafts list changes
  useEffect(() => {
    if (!isOpen || drafts.length === 0) return;
    const initial: Record<string, Decision> = {};
    for (const draft of drafts) {
      initial[draft.id] = draft.workflowStatus === 'active' ? 'carry-forward' : 'do-not-carry';
    }
    setDecisions(initial);
  }, [isOpen, drafts]);

  const applyAll = (decision: Decision): void => {
    const next: Record<string, Decision> = {};
    for (const draft of drafts) {
      next[draft.id] = decision;
    }
    setDecisions(next);
  };

  const allDecided = drafts.every((d) => decisions[d.id] !== undefined);

  const handleConfirm = (): void => {
    void onConfirm(decisions);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      size="md"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <p className="text-base font-semibold">Unresolved Trades</p>
          <p className="text-sm font-normal text-text-secondary">
            Decide what happens to these trades when the session closes.
          </p>
        </ModalHeader>

        <ModalBody className="gap-3">
          {/* Bulk shortcuts — only shown when > 1 draft */}
          {drafts.length > 1 && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="flat"
                color="primary"
                onPress={() => applyAll('carry-forward')}
              >
                All: Carry Forward
              </Button>
              <Button
                size="sm"
                variant="flat"
                color="default"
                onPress={() => applyAll('do-not-carry')}
              >
                All: Discard
              </Button>
            </div>
          )}

          {/* Per-draft rows */}
          {drafts.map((draft) => {
            const decision = decisions[draft.id];
            const name = draft.tradeName ?? 'Unnamed Trade';
            const isActive = draft.workflowStatus === 'active';

            return (
              <div
                key={draft.id}
                className="flex flex-col gap-2 p-3 rounded-lg border border-default-200 bg-default-50"
              >
                {/* Name + status badge */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary flex-1 truncate">
                    {name}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                    isActive
                      ? 'bg-success-100 text-success-700'
                      : 'bg-default-100 text-text-secondary'
                  }`}>
                    {isActive ? 'Active' : 'Pending'}
                  </span>
                </div>

                {/* Decision buttons */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={decision === 'carry-forward' ? 'solid' : 'bordered'}
                    color={decision === 'carry-forward' ? 'primary' : 'default'}
                    className="flex-1"
                    isDisabled={isCommitting}
                    onPress={() => setDecisions((prev) => ({ ...prev, [draft.id]: 'carry-forward' }))}
                  >
                    Carry Forward
                  </Button>
                  <Button
                    size="sm"
                    variant={decision === 'do-not-carry' ? 'solid' : 'bordered'}
                    color={decision === 'do-not-carry' ? 'danger' : 'default'}
                    className="flex-1"
                    isDisabled={isCommitting}
                    onPress={() => setDecisions((prev) => ({ ...prev, [draft.id]: 'do-not-carry' }))}
                  >
                    Discard
                  </Button>
                </div>
              </div>
            );
          })}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="light"
            onPress={onClose}
            isDisabled={isCommitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="solid"
            isLoading={isCommitting}
            isDisabled={isCommitting || !allDecided}
            onPress={handleConfirm}
          >
            Confirm &amp; Continue →
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
